// Generic-CSV column → role mapping (Phase 3, spec §9).
//
// Lockstep with evals/adapters/_column_mapping.py — same synonyms, same
// priority, same projection, so a mapping proposed in the browser matches one
// proposed server-side. The convenience path for an arbitrary tabular CSV that
// no known adapter (OFW-CSV, Civil Communicator, the canonical generic schema)
// claims: fuzzy-match the file's headers onto canonical roles, let the user
// confirm/edit, then project rows to the canonical six-column shape.
//
// Roles: required = sender, timestamp, body; optional = recipient, subject.
// A mapping is { role: header | null }. Every role is always a key (so the UI
// renders one dropdown per role); null = nothing matched.
//
// Scope note (deferred): the generic path parses ONE column as the timestamp
// (flexible). Composite multi-column timestamps (OFW's separate Date + Time)
// are out of scope here — that's why OFW-CSV is a dedicated adapter. See BACKLOG.

export const REQUIRED_ROLES = ["sender", "timestamp", "body"] as const;
export const OPTIONAL_ROLES = ["recipient", "subject"] as const;
export const ALL_ROLES = [...REQUIRED_ROLES, ...OPTIONAL_ROLES] as const;
export type Role = (typeof ALL_ROLES)[number];

export const CANONICAL_COLUMNS = [
  "timestamp",
  "sender",
  "recipient",
  "subject",
  "body",
  "attachments",
] as const;

export type ColumnMapping = Record<Role, string | null>;

// Synonyms per role, priority order. Resolution order resolves body first (so
// "message"/"text" win the body slot), then sender, timestamp, then optional
// roles. Lockstep with _column_mapping.py _SYNONYMS / _RESOLVE_ORDER.
const SYNONYMS: Record<Role, string[]> = {
  body: ["body", "message", "text", "content", "msg"],
  sender: ["sender", "from", "author", "sent by", "sentby"],
  timestamp: ["timestamp", "datetime", "date/time", "date", "sent", "time", "when"],
  recipient: ["recipient", "to", "receiver", "addressed to"],
  subject: ["subject", "title", "topic", "re"],
};
const RESOLVE_ORDER: Role[] = ["body", "sender", "timestamp", "recipient", "subject"];

function norm(s: string): string {
  return s.trim().toLowerCase().replace(/[_-]/g, " ").split(/\s+/).filter(Boolean).join(" ");
}

function scoreHeader(normHeader: string, synonyms: string[]): number {
  const tokens = new Set(normHeader.split(" "));
  for (const syn of synonyms) {
    if (normHeader === syn) return 2;
  }
  for (const syn of synonyms) {
    const synTokens = syn.split(" ");
    if (synTokens.length === 1) {
      if (tokens.has(syn)) return 1;
    } else if (normHeader.includes(syn)) {
      return 1;
    }
  }
  return 0;
}

/** Propose a best-guess { role: header | null } mapping. Each source header is
 *  used at most once; required roles resolved before optional ones. */
export function proposeMapping(headers: string[]): ColumnMapping {
  const normed = new Map(headers.map((h) => [h, norm(h)]));
  const used = new Set<string>();
  const mapping = Object.fromEntries(ALL_ROLES.map((r) => [r, null])) as ColumnMapping;

  for (const role of RESOLVE_ORDER) {
    const synonyms = SYNONYMS[role];
    let bestHeader: string | null = null;
    let bestScore = 0;
    for (const h of headers) {
      if (used.has(h)) continue;
      const score = scoreHeader(normed.get(h) ?? "", synonyms);
      if (score > bestScore) {
        bestScore = score;
        bestHeader = h;
      }
    }
    if (bestHeader !== null && bestScore > 0) {
      mapping[role] = bestHeader;
      used.add(bestHeader);
    }
  }
  return mapping;
}

/** True when every required role has a chosen header. */
export function isComplete(mapping: ColumnMapping): boolean {
  return REQUIRED_ROLES.every((role) => Boolean(mapping[role]));
}

/** A stable, order-insensitive, lower-cased signature of a header set, used to
 *  key a saved mapping profile so re-importing the same shape skips the step. */
export function headerSignature(headers: string[]): string {
  return headers
    .map((h) => norm(h))
    .sort()
    .join("|");
}

export interface CanonicalRowDict {
  timestamp: string;
  sender: string;
  recipient: string;
  subject: string;
  body: string;
  attachments: string;
}

/** Project raw CSV row-dicts into the canonical six-column shape. Unmatched
 *  optional roles become "". `attachments` is always "". `limit` truncates for
 *  a live preview. Values pass through verbatim except non-body roles trim. */
export function projectRows(
  rows: Record<string, string>[],
  mapping: ColumnMapping,
  limit?: number,
): CanonicalRowDict[] {
  const src = limit === undefined ? rows : rows.slice(0, limit);
  return src.map((r) => {
    const out: CanonicalRowDict = {
      timestamp: "",
      sender: "",
      recipient: "",
      subject: "",
      body: "",
      attachments: "",
    };
    for (const role of ALL_ROLES) {
      const header = mapping[role];
      if (header != null) {
        const raw = r[header] ?? "";
        out[role] = role === "body" ? raw : raw.trim();
      }
    }
    return out;
  });
}
